import { useEffect, useRef } from 'react'
import {
    getElementExplainContext,
    buildExplainPrompt,
    type ElementExplainContext,
} from '../explainUtils'

const BODY_CLASS = 'eid-explain-mode'
const STYLE_ID = 'eid-explain-mode-styles'

const HIGHLIGHT_CSS = `
  /* Parent containers with nested explainable children — dashed to distinguish */
  body.${BODY_CLASS} [data-ai-explain]:has([data-ai-explain]) {
    outline: 2px dashed var(--main-accent, #3b82f6) !important;
    outline-offset: 0;
    border-radius: 2px;
    cursor: pointer;
    box-sizing: border-box;
  }
  /* Leaf explainable elements — solid, more prominent */
  body.${BODY_CLASS} [data-ai-explain]:not(:has([data-ai-explain])) {
    outline: 2px solid var(--main-accent, #3b82f6) !important;
    outline-offset: 0;
    border-radius: 2px;
    cursor: pointer;
    box-sizing: border-box;
  }
  /* Hover for both */
  body.${BODY_CLASS} [data-ai-explain]:hover {
    outline-color: var(--main-accent-hover, #2563eb);
    background-color: var(--info-bg, rgba(59, 130, 246, 0.06));
  }
`

function injectHighlightStyles(): void {
    if (typeof document === 'undefined' || document.getElementById(STYLE_ID))
        return
    const style = document.createElement('style')
    style.id = STYLE_ID
    style.textContent = HIGHLIGHT_CSS
    document.head.appendChild(style)
}

export interface UseExplainModeOptions {
    /** When true, explainable elements are highlighted and clicks trigger explain. */
    active: boolean
    /** Called when user clicks an element with data-ai-explain; receives the prompt to send. */
    onExplainElement: (context: ElementExplainContext, prompt: string) => void
}

/**
 * Manages AI-Explain mode: when active, adds body class and highlight styles for [data-ai-explain],
 * and listens for clicks on those elements to gather context and call onExplainElement.
 */
export function useExplainMode({
    active,
    onExplainElement,
}: UseExplainModeOptions): void {
    const onExplainRef = useRef(onExplainElement)
    onExplainRef.current = onExplainElement

    useEffect(() => {
        if (!active) {
            document.body.classList.remove(BODY_CLASS)
            return
        }
        injectHighlightStyles()
        document.body.classList.add(BODY_CLASS)

        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement
            const el = target.closest?.('[data-ai-explain]') as HTMLElement | null
            if (!el) return
            e.preventDefault()
            e.stopPropagation()
            const context = getElementExplainContext(el)
            const prompt = buildExplainPrompt(context)
            onExplainRef.current(context, prompt)
        }

        document.addEventListener('click', handleClick, true)
        return () => {
            document.removeEventListener('click', handleClick, true)
            document.body.classList.remove(BODY_CLASS)
        }
    }, [active])
}

export default useExplainMode
